import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  PROVIDER_TYPES, connectRepository, disconnectRepository, getPublishChoice, listConnections, originOf, providerTypeOf, saveToken,
  savePublishChoice, testRepository,
} from '../services/GitSourceService';
import { consoleWebUrl } from '../services/ConsoleNextService';

// Publish › Mia-Platform Console, two questions in plain words, each with a default that needs nothing:
// "Where does the code live?" (Flow's own git, or the person's repository) and "Where are the images built?" (Console Next
// with BuildKit, or the person's CI/CD). The choice is remembered per project (`.mia/project.json` › `publish`).

export const CI_PROVIDERS = [
  { key: 'gitlab', label: 'GitLab CI' },
  { key: 'github', label: 'GitHub Actions' },
  { key: 'azure', label: 'Azure Pipelines' },
  { key: 'bitbucket', label: 'Bitbucket Pipelines' },
  { key: 'jenkins', label: 'Jenkins' },
  { key: 'webhook', label: 'Another tool (webhook)' },
];

const providerLabel = (key) => (CI_PROVIDERS.find((provider) => provider.key === key) || {}).label || null;

/** The Console Next page where a Project's build and CI settings live (the route of its Build & CI settings). */
export const buildSettingsUrl = (projectName) => consoleWebUrl(`/projects/${encodeURIComponent(projectName)}?tab=settings&section=build`);

export const EMPTY_FORM = { code: 'flow', build: 'console-next', ciProvider: null, repos: {}, token: '', username: '', type: '', accountIds: {} };

/** One line for the Publish summary: `Code: In Flow's git. Images: Console Next with BuildKit.` */
export function journeySummary(form) {
  const code = form.code === 'repository' ? 'in your repository' : 'in Flow\'s git';
  const build = form.build === 'ci' ? `your CI/CD${providerLabel(form.ciProvider) ? ` (${providerLabel(form.ciProvider)})` : ''}` : 'Console Next with BuildKit';
  return { code, build };
}

/** The form from what is remembered and what is connected: services with a connection prefill their address and branch. */
function initialForm(choice, connections) {
  const repos = {};
  for (const connection of connections) { repos[connection.service] = { url: connection.url, branch: connection.branch || '', credentialRef: connection.credentialRef || null }; }
  return { ...EMPTY_FORM, code: connections.length ? 'repository' : choice.code, build: choice.build, ciProvider: choice.ciProvider, repos };
}

/** What is wrong with the form, in words; null when it can go. */
export function journeyProblem(form) {
  if (form.code === 'repository') {
    const entries = Object.values(form.repos).filter((repo) => repo.url && repo.url.trim());
    if (!entries.length) { return 'Give the address of your repository, or keep the code in Flow.'; }
    const bad = entries.find((repo) => !originOf(repo.url));
    if (bad) { return 'A repository address starts with https://.'; }
  }
  if (form.build === 'ci' && !form.ciProvider) { return 'Pick the CI/CD you use.'; }
  return null;
}

/**
 * Makes the form true on the server before the delivery: the token becomes one of the person's accounts (once per host),
 * each repository is connected (or disconnected when the code stays in Flow), and the choice is remembered.
 * @returns {Promise<void>} rejects with a readable message
 */
export async function applyJourney(projectId, form, services, connections = []) {
  if (form.code === 'flow') {
    for (const connection of connections) {
      // eslint-disable-next-line no-await-in-loop -- one at a time: the project's file is rewritten for each
      await disconnectRepository(projectId, connection.service);
    }
  } else {
    const accountIds = { ...form.accountIds };
    for (const service of services) {
      const repo = form.repos[service];
      const existing = connections.find((connection) => connection.service === service);
      if (!repo || !repo.url || !repo.url.trim()) {
        // eslint-disable-next-line no-await-in-loop
        if (existing) { await disconnectRepository(projectId, service); }
        // eslint-disable-next-line no-continue
        continue;
      }
      const origin = originOf(repo.url);
      let credentialRef = repo.credentialRef || null;
      if (form.token.trim()) {
        if (!accountIds[origin]) {
          // eslint-disable-next-line no-await-in-loop
          accountIds[origin] = (await saveToken({ type: form.type || providerTypeOf(repo.url), url: repo.url, token: form.token.trim(), username: form.username.trim() })).id;
        }
        credentialRef = accountIds[origin];
      }
      // eslint-disable-next-line no-await-in-loop
      await connectRepository(projectId, { service, url: repo.url.trim(), credentialRef, branch: repo.branch.trim() });
    }
  }
  await savePublishChoice(projectId, form);
}

function Choice({ checked, children, hint, id, label, name, onChange, testId }) {
  return (
    <div className={`publish-choice${checked ? ' is-on' : ''}`}>
      <label className="publish-choice-head" htmlFor={id}>
        <input checked={checked} data-testid={testId} id={id} name={name} onChange={onChange} type="radio" />
        <span>
          <b>{label}</b>
          <small>{hint}</small>
        </span>
      </label>
      {checked && children}
    </div>
  );
}

Choice.propTypes = {
  checked: PropTypes.bool.isRequired,
  children: PropTypes.node,
  hint: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  testId: PropTypes.string,
};

/**
 * @param {object} props
 * @param {string} props.projectId
 * @param {string} props.projectName the name Console Next knows the Project by (its build settings page)
 * @param {string[]} props.services
 * @param {(state: { form: object, connections: object[], problem: string|null, ready: boolean }) => void} props.onChange
 */
export default function PublishJourney({ projectId, projectName, services, onChange }) {
  const [form, setForm] = useState(null);
  const [connections, setConnections] = useState([]);
  const [tests, setTests] = useState({});

  useEffect(() => {
    let live = true;
    Promise.all([getPublishChoice(projectId).catch(() => ({ code: 'flow', build: 'console-next', ciProvider: null })), listConnections(projectId).catch(() => [])])
      .then(([choice, connected]) => {
        if (!live) { return; }
        setConnections(connected);
        setForm(initialForm(choice, connected));
      });
    return () => { live = false; };
  }, [projectId]);

  useEffect(() => {
    if (form) { onChange({ form, connections, problem: journeyProblem(form), ready: true }); }
  }, [form, connections, onChange]);

  const update = useCallback((patch) => setForm((prev) => ({ ...prev, ...patch })), []);
  const setRepo = (service, patch) => setForm((prev) => ({ ...prev, repos: { ...prev.repos, [service]: { url: '', branch: '', credentialRef: null, ...prev.repos[service], ...patch } } }));
  const guessed = useMemo(() => {
    const first = Object.values(form?.repos || {}).find((repo) => repo.url);
    return first ? providerTypeOf(first.url) : null;
  }, [form]);

  if (!form) { return <p className="publish-note" data-testid="publish-journey-loading">Loading your choices…</p>; }

  const test = async (service) => {
    const repo = form.repos[service];
    setTests((prev) => ({ ...prev, [service]: { busy: true } }));
    try {
      let credentialRef = repo.credentialRef || null;
      let accountIds = form.accountIds;
      const origin = originOf(repo.url);
      if (form.token.trim() && origin) {
        if (!accountIds[origin]) {
          const saved = await saveToken({ type: form.type || providerTypeOf(repo.url), url: repo.url, token: form.token.trim(), username: form.username.trim() });
          accountIds = { ...accountIds, [origin]: saved.id };
          update({ accountIds });
        }
        credentialRef = accountIds[origin];
      }
      const answer = await testRepository(projectId, { url: repo.url.trim(), credentialRef });
      setRepo(service, { credentialRef });
      setTests((prev) => ({ ...prev, [service]: { ok: true, defaultBranch: answer.defaultBranch, branches: answer.branches } }));
    } catch (error) {
      setTests((prev) => ({ ...prev, [service]: { ok: false, message: error.message } }));
    }
  };

  const type = form.type || guessed || '';
  return (
    <div className="publish-journey" data-testid="publish-journey">
      <fieldset className="publish-question">
        <legend>Where does the code live?</legend>
        <Choice
          checked={form.code === 'flow'}
          hint="Nothing to set up: Flow keeps your code in its own git."
          id="publish-code-flow"
          label="In Flow (default)"
          name="publish-code"
          onChange={() => update({ code: 'flow' })}
          testId="publish-code-flow"
        />
        <Choice
          checked={form.code === 'repository'}
          hint="Flow sends your code to your repository (GitHub, GitLab, Azure DevOps, Bitbucket, any git over https). It never overwrites what is there."
          id="publish-code-repository"
          label="In my repository"
          name="publish-code"
          onChange={() => update({ code: 'repository' })}
          testId="publish-code-repository"
        >
          <span className="publish-repos">
            {services.map((service) => {
              const repo = form.repos[service] || { url: '', branch: '' };
              const result = tests[service];
              return (
                <span className="publish-repo" data-testid={`publish-repo-${service}`} key={service}>
                  <span className="publish-repo-name">{service}</span>
                  <input aria-label={`Repository of ${service}`} onChange={(event) => setRepo(service, { url: event.target.value })} placeholder="https://gitlab.com/your-team/your-repo.git" value={repo.url} />
                  <input aria-label={`Branch of ${service}`} className="publish-repo-branch" onChange={(event) => setRepo(service, { branch: event.target.value })} placeholder="branch (the current one)" value={repo.branch} />
                  <button className="publish-btn" disabled={!originOf(repo.url) || result?.busy} onClick={() => test(service)} type="button">{result?.busy ? 'Testing…' : 'Test'}</button>
                  {result && !result.busy && (
                    <span className={`publish-test ${result.ok ? 'publish-test-ok' : 'publish-test-bad'}`} data-testid={`publish-test-${service}`} role="status">
                      {result.ok ? `Reachable${result.defaultBranch ? `: its default branch is ${result.defaultBranch}` : ''}.` : result.message}
                    </span>
                  )}
                </span>
              );
            })}
            <span className="publish-field">
              <label htmlFor="publish-token">Access token</label>
              <input autoComplete="off" id="publish-token" onChange={(event) => update({ token: event.target.value })} placeholder="Paste a token with write access to the repository" type="password" value={form.token} />
              <small>Kept in your Flow connections (Features › Connections), never in the project. Leave it empty to use the account you already connected.</small>
            </span>
            <span className="publish-field">
              <label htmlFor="publish-provider">Provider</label>
              <select id="publish-provider" onChange={(event) => update({ type: event.target.value })} value={type}>
                <option value="">Detect from the address</option>
                {PROVIDER_TYPES.map((provider) => <option key={provider.key} value={provider.key}>{provider.label}</option>)}
              </select>
            </span>
            {(type === 'bitbucket-dc') && (
              <span className="publish-field">
                <label htmlFor="publish-username">Bitbucket username</label>
                <input id="publish-username" onChange={(event) => update({ username: event.target.value })} value={form.username} />
              </span>
            )}
          </span>
        </Choice>
      </fieldset>

      <fieldset className="publish-question">
        <legend>Where are the images built?</legend>
        <Choice
          checked={form.build === 'console-next'}
          hint="No other system needed."
          id="publish-build-console"
          label="Console Next with BuildKit (default)"
          name="publish-build"
          onChange={() => update({ build: 'console-next' })}
          testId="publish-build-console"
        />
        <Choice
          checked={form.build === 'ci'}
          hint="Console Next starts the pipeline you already have and waits for its result."
          id="publish-build-ci"
          label="My CI/CD"
          name="publish-build"
          onChange={() => update({ build: 'ci' })}
          testId="publish-build-ci"
        >
          <span className="publish-repos">
            <span className="publish-field">
              <label htmlFor="publish-ci">CI/CD</label>
              <select data-testid="publish-ci-provider" id="publish-ci" onChange={(event) => update({ ciProvider: event.target.value || null })} value={form.ciProvider || ''}>
                <option value="">Pick one</option>
                {CI_PROVIDERS.map((provider) => <option key={provider.key} value={provider.key}>{provider.label}</option>)}
              </select>
            </span>
            <a className="publish-link-out" data-testid="publish-ci-setup" href={buildSettingsUrl(projectName)} rel="noopener noreferrer" target="_blank">Set it up in Console Next</a>
          </span>
        </Choice>
      </fieldset>
    </div>
  );
}

PublishJourney.propTypes = {
  onChange: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
  projectName: PropTypes.string.isRequired,
  services: PropTypes.arrayOf(PropTypes.string),
};

PublishJourney.defaultProps = { services: [] };
